import { getFunctionAuthHeaders, getFunctionsBaseUrl } from 'utils/getFunctionsBaseUrl';
import { getCookie } from 'utils/tokens';
import { apiHost, deleteCall, get, post } from '.';
import type { ProposalChangesResponse } from '../types/proposalChanges';
import { StreamResponse } from '../types/stream';
import VenueSearchFetcher from './VenueSearchFetcher';
import { createInquiryDraft } from './inquiry';

export { getReviewText } from '../types/googleReviews';
export type { GoogleReview } from '../types/googleReviews';

export const DEBOUNCE_DELAY = 1000;

export const loadSuggestedVenuesForEvent = (id: string | number, facets?: BizlyAPI.VenueFacets) =>
    new VenueSearchFetcher(id, { facets, perPage: 20 }).getNextPage();

export const getVenueInquiries = (eventId: string | number): Promise<Bizly.Venue[]> =>
    (get(`events/${eventId}/venues`) as Promise<{ venues: Bizly.Venue[] }>).then(res =>
        res.venues.map(v => ({
            ...v,
            status: v.booking && v.status === 'Inquiry Cancelled' ? 'Booking Cancelled' : v.status,
        }))
    );

export const getInquiry = (inquiryId: number) =>
    get(`booking-inquiries/${inquiryId}`) as Promise<{ inquiry: BizlyAPI.Inquiry }>;

export const saveInquiry = (inquiryId: number) =>
    post(`booking-inquiries/${inquiryId}/save`) as Promise<{ inquiry: BizlyAPI.Inquiry }>;

export const addVenueToInquiry = (inquiryId: number, venueId: number) =>
    post(`booking-inquiries/${inquiryId}/venues`, { venue_id: venueId }) as Promise<{ inquiry: BizlyAPI.Inquiry }>;

export const addVenueToInquiryDraft = async (
    eventId: number,
    { inquiryId, venueId }: { inquiryId?: number; venueId: number }
) => {
    // put
    if (inquiryId) return await addVenueToInquiry(inquiryId, venueId);

    // post
    const { inquiry } = await createInquiryDraft(eventId);
    const draftId = inquiry.id;
    return await addVenueToInquiry(draftId, venueId);
};

export { createInquiryDraft, removeVenueFromInquiry, submitInquiry } from './inquiry';

export const selectVenue = (event: Bizly.Event, venue: Bizly.Venue) =>
    post(`events/${event.id}/venues`, { venue_id: venue.id });

export const deselectVenue = (event: Bizly.Event, venue: Bizly.Venue) =>
    deleteCall(`events/${event.id}/venues/${venue.id}`);

export const searchVenues = (
    eventId?: string | number,
    facets = {} as BizlyAPI.VenueFacets,
    pageConfig = {},
    nameQuery?: string
): Promise<{
    venues: Bizly.Venue[];
    meta: { lastPage: number; total: number; travelstarCounts?: Record<number, number> };
}> =>
    post(`venues/search`, {
        ...(eventId ? { eventId } : {}),
        ...facets,
        page: 1,
        perPage: 10,
        ...pageConfig,
        ...(nameQuery ? { q: nameQuery } : {}),
    }).then(res => ({
        venues: res?.venues || [],
        meta: {
            lastPage: 0,
            total: 0,
            ...res?.searchMeta,
            travelstarCounts: res?.searchMeta?.travelstarCounts,
        },
    }));

// Utility function to validate venue ID
const validateVenueId = (venueId: number | string): number => {
    const id = typeof venueId === 'string' ? parseInt(venueId, 10) : venueId;
    if (isNaN(id) || id <= 0) {
        throw new Error(`Invalid venue ID: ${venueId}`);
    }
    return id;
};

export const getVenue = (venueId: number, section?: string) => {
    const validId = validateVenueId(venueId);
    return get(`venues/${validId}${section ? `?section=${section}` : ''}`, { showErrorCode: true });
};

export const getVenueByGooglePlaceId = (googlePlaceId: string) => get(`venues/google-place/${googlePlaceId}`);

/** Token-free variant — use this on public/unauthenticated pages (e.g. Discover) so the
 *  find-or-create from a Google suggestion works without an auth token. */
export const getPublicVenueByGooglePlaceId = (googlePlaceId: string) =>
    get(`venues/public/google-place/${googlePlaceId}`, { requireToken: false });

/**
 * Heals a venue's broken (403/404) Google photos by capturing them into Cloudinary on the
 * backend and returning the durable image set. Called from a venue image's onError handler.
 */
export const refreshVenueGoogleImage = (venueId: number): Promise<{ success: boolean; images: Bizly.VenueImage[] }> => {
    const validId = validateVenueId(venueId);
    return post(`venues/${validId}/images/refresh-google`);
};

export const getVenueSpaces = (venueId: number) => {
    const validId = validateVenueId(venueId);
    return get(`venues/${validId}/spaces`);
};

export const getProposalsForInquiry = (inquiryId: number) =>
    get(`booking-inquiries/${inquiryId}/proposals`) as Promise<{
        success?: boolean;
        proposals: Bizly.VenueProposal[];
        meta?: { total: number };
    }>;

// TODO: We are calling this same endpoint (not helper, but the literal endpoint) more often than we should. We need to revisit the proposal's page and set it up to GET a single proposal only once, using only one function.
export const getVenueProposal = (proposalId: number | string) => get(`proposals/${proposalId}`);

export const getProposalChanges = (proposalId: number | string) =>
    get(`proposals/${proposalId}/changes`) as Promise<ProposalChangesResponse>;

export const getVenueProposalAccepted = (proposalId: number): Promise<Bizly.VenueProposal | undefined> =>
    get(`proposals/${proposalId}?include=booking,contact,payments`).then(res => res.proposal);

export type RejectReason = {
    proposalId: number;
    rejectionReason: string;
};

export type AcceptProposalPayload = {
    reasonNonPreferredChosen?: string;
    rejectReasons?: RejectReason[];
};

export const acceptProposal = (proposalId: number | string, payload?: AcceptProposalPayload) =>
    post(`proposals/${proposalId}/accept`, payload);

export const requestExtendProposal = (proposalId: number | string) => post(`proposals/${proposalId}/request-extension`);

export const rejectProposal = (proposalId: number | string, payload?: { rejectionReason?: string }) =>
    post(`proposals/${proposalId}/reject`, payload);

export const acceptProposalRebid = (proposalId: number | string) =>
    post(`proposals/${proposalId}/rebid/accept`) as Promise<{ success: boolean; proposal: Bizly.VenueProposal }>;

export const declineProposalRebid = (proposalId: number | string, payload: { reason?: string }) =>
    post(`proposals/${proposalId}/rebid/decline`, payload) as Promise<{ success: boolean }>;

export const confirmContractAcceptance = (proposalId: number | string) =>
    post(`proposals/${proposalId}/confirm-contract-acceptance`);

export const requestContractChange = (proposalId: number | string, payload: { changeRequestNote: string }) =>
    post(`proposals/${proposalId}/contract-change-request`, payload);

const parseFilenameFromContentDisposition = (contentDisposition: string | null): string | undefined => {
    if (!contentDisposition) return undefined;
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match) return decodeURIComponent(utf8Match[1]);
    return /filename="?([^";]+)"?/i.exec(contentDisposition)?.[1];
};

export const downloadServiceAgreement = async (
    venueId: number | string,
    proposalId: number | string
): Promise<{ blob: Blob; filename?: string }> => {
    const response = await fetch(`${apiHost}/venues/${venueId}/proposals/${proposalId}/service-agreement/download`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${getCookie()}` },
    });

    if (!response.ok) {
        throw new Error(`Failed to download service agreement (${response.status})`);
    }

    const blob = await response.blob();
    const filename = parseFilenameFromContentDisposition(response.headers.get('Content-Disposition'));
    return { blob, filename };
};

export const getProposalMessages = (proposalId?: number | string) =>
    get(`proposals/${proposalId}/notes`, { requireToken: true }) as Promise<{
        notes: BizlyAPI.VenueMessage[];
        venue: BizlyAPI.ProposalNotesVenue;
    }>;

export const sendProposalMessage = (
    proposalId?: number | string,
    message?: BizlyAPI.VenueMessage.Text | BizlyAPI.VenueMessage.Attachment
) => post(`proposals/${proposalId}/notes`, message) as Promise<{ note: BizlyAPI.VenueMessage }>;

export const getProposalMessagesByVenue = (venueId: number | string, proposalId: number | string) =>
    get(`venues/${venueId}/proposals/${proposalId}/notes`, { requireToken: true }) as Promise<{
        notes: BizlyAPI.VenueMessage[];
        venue: BizlyAPI.ProposalNotesVenue;
    }>;

export const sendProposalMessageByVenue = (
    venueId: number | string,
    proposalId: number | string,
    message: BizlyAPI.VenueMessage.Text | BizlyAPI.VenueMessage.Attachment
) =>
    post(`venues/${venueId}/proposals/${proposalId}/notes`, message) as Promise<{
        note: BizlyAPI.VenueMessage;
    }>;

export const nudge = (bookingInquiryId: number, venueInquiryId: number) =>
    post(`booking-inquiries/${bookingInquiryId}/venues/${venueInquiryId}/nudge`) as Promise<{
        success: boolean;
        inquiry: BizlyAPI.Inquiry;
    }>;

export const getVenueInsights = async (venueId: number, section: string): Promise<StreamResponse> => {
    const baseUrl = getFunctionsBaseUrl();
    try {
        const response = await fetch(`${baseUrl}venue-insights-stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...getFunctionAuthHeaders(),
            },
            body: JSON.stringify({ venueId, section }),
            credentials: 'same-origin',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching venue insights:', error);
        throw error;
    }
};

export const getVenueReviewsSummary = async (venueId: number): Promise<StreamResponse> => {
    const baseUrl = getFunctionsBaseUrl();
    try {
        const response = await fetch(`${baseUrl}venue-reviews-summary`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getFunctionAuthHeaders(),
            },
            body: JSON.stringify({ venueId }),
            credentials: 'same-origin',
        });
        if (!response.ok) {
            throw Object.assign(new Error(`HTTP error! status: ${response.status}`), { status: response.status });
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching venue reviews summary:', error);
        throw error;
    }
};

export const getPublicVenue = (venueId: number, section?: string) => {
    const validId = validateVenueId(venueId);
    return get(`venues/public/${validId}${section ? `?section=${section}` : ''}`, {
        requireToken: false,
        showErrorCode: true,
    });
};

export const publicVenueSearch = (
    facets = {} as BizlyAPI.VenueFacets,
    pageConfig = {},
    nameQuery?: string
): Promise<{
    venues: Bizly.Venue[];
    meta: { lastPage: number; total: number; travelstarCounts?: Record<number, number> };
}> =>
    post(
        `venues/public/search`,
        {
            ...facets,
            page: 1,
            perPage: 10,
            ...pageConfig,
            ...(nameQuery ? { q: nameQuery } : {}),
        },
        { requireToken: false }
    ).then(res => ({
        venues: res?.venues || [],
        meta: {
            lastPage: 0,
            total: 0,
            ...res?.searchMeta,
            travelstarCounts: res?.searchMeta?.travelstarCounts,
        },
    }));

export const checkVenueImagePreview = async (imageLink: string) => {
    const response = await fetch('/.netlify/functions/check-venue-image-preview', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getFunctionAuthHeaders(),
        },
        body: JSON.stringify({ imageLink }),
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};
